import classNames from 'classnames'
import { useMemo } from 'react'
import { MdFullscreen } from 'react-icons/md'

import type { ReactElement } from 'react'

import AppLogo from '~/components/AppLogo'
import Button from '~/components/Buttons/Button'
import DateInput from '~/components/Inputs/Basic/DateInput'
import MultiSelectInput from '~/components/Inputs/Basic/MultiSelectInput'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import CustomControl from '~/components/SelectControls/CustomControl'
import CustomAppOption, { CustomAppControl } from '~/components/SelectOptions/CustomAppOption'
import CustomImageOption from '~/components/SelectOptions/CustomImageOption'
import { MAX_DAYS_RANGE, TODAY } from '~/const'
import { countryWithFlagOptions, platformOptions } from '~/const/options'
import { aggregationOptions, topCountriesCountOptions } from '~/const/user-acquisition'
import useApps from '~/hooks/useApps'
import { useUADashboardConfigQuery } from '~/queries/user-acquisition'
import dayjs from '~/utils/dayjs'
import { formatAndSortOptionsFromStrings, startsWithFilter } from '~/utils/options'

import './styles.scss'

export interface IUAFilters extends Record<string, any> {
  aggregations: IOption[]
  app_key: IOption[] | null
  country: IOption[] | null
  device_type: IOption | null
  end_date: string
  start_date: string
  top_countries_count: IOption | null
  ua_adset_id: IOption | null
  ua_campaign_id: IOption | null
  ua_source: IOption | null
}

interface Props {
  filters: IUAFilters
  isFullScreen: boolean
  isSubmitButtonDisabled?: boolean
  onDateChanged: (dateRange: string[]) => void
  onFilterChanged: (key: keyof IUAFilters, value: IOption | IOption[] | null) => void
  onFullScreen: VoidFunction
  onReset: VoidFunction
  onSubmit: VoidFunction
}

interface IAppOption extends IOption {
  url?: string
}

const appKeyShortcuts = [
  'edjing_mix',
  'learning_sampler',
  'procolor',
  'learning_piano',
  'stems',
  'learning_guitar',
  'swipewipe',
]

const countryCodeShortcuts = ['US', 'GB', 'CA', 'JP', 'AU', 'FR', 'DE', 'CN', 'BR']

const UAFilters = ({
  filters,
  isFullScreen,
  isSubmitButtonDisabled = false,
  onDateChanged,
  onFilterChanged,
  onFullScreen,
  onReset,
  onSubmit,
}: Props): ReactElement => {
  /** Local state */

  const { appKeys, isAppsLoading } = useApps()

  const appKeyOptions = appKeys.map(({ appKey, url }) => ({ label: appKey, url, value: appKey }))

  const applicationShortcuts = appKeyShortcuts.reduce((prev, appKey) => {
    const option = appKeyOptions.find(({ value }) => value === appKey)
    if (option) {
      prev.push(option)
    }
    return prev
  }, [] as IAppOption[])

  const showTopCountries = filters.aggregations.find(({ value }) => value === 'country')

  const countryShortcuts = countryCodeShortcuts.reduce((prev, code) => {
    const option = countryWithFlagOptions.find(({ value }) => value === code)
    if (option) {
      prev.push(option)
    }
    return prev
  }, [] as IOption[])

  /** Queries & Mutations */

  const { data } = useUADashboardConfigQuery()

  const adsetOptions = useMemo(
    () => data?.ua_adsets.map(({ ua_adset_id, ua_adset_name }) => ({ label: ua_adset_name, value: ua_adset_id })),
    [data?.ua_adsets]
  )

  const campaignOptions = useMemo(
    () =>
      data?.ua_campaigns.map(({ ua_campaign_id, ua_campaign_name }) => ({
        label: ua_campaign_name,
        value: ua_campaign_id,
      })),
    [data?.ua_campaigns]
  )
  const sourceOptions = useMemo(() => formatAndSortOptionsFromStrings(data?.ua_sources || []), [data?.ua_sources])

  /** Handlers */

  const handleDateModeClick = (mode: 'd7' | 'd10' | 'd30') => () => {
    let start = filters.start_date
    let end = filters.end_date
    if (mode === 'd7') {
      start = dayjs().add(-38, 'days').format('YYYY-MM-DD')
      end = dayjs().add(-8, 'days').format('YYYY-MM-DD')
    } else if (mode === 'd10') {
      start = dayjs().add(-41, 'days').format('YYYY-MM-DD')
      end = dayjs().add(-11, 'days').format('YYYY-MM-DD')
    } else if (mode === 'd30') {
      start = dayjs().add(-61, 'days').format('YYYY-MM-DD')
      end = dayjs().add(-31, 'days').format('YYYY-MM-DD')
    }

    onDateChanged([start, end])
  }

  const handleShortcutClick = (key: string, option: IOption) => {
    let newOptions: IOption[] = filters[key]
    if (newOptions.filter(({ value }) => option.value === value).length === 0) {
      newOptions.push(option)
    } else {
      newOptions = [...newOptions.filter(({ value }) => value !== option.value)]
    }
    onFilterChanged(key, newOptions)
  }

  /** Render */

  return (
    <div className={classNames('ua-filters', { 'ua-filters--fullscreen': isFullScreen })}>
      <div className="ua-filters__content">
        <div className="filters-container">
          <div className="filters-row">
            <div className="input-with-shortcuts">
              <MultiSelectInput
                components={{ Control: CustomAppControl, Option: CustomAppOption }}
                isClearable
                isLoading={isAppsLoading}
                isSearchable
                label="Applications"
                onChange={(option) => onFilterChanged('app_key', option as IOption[])}
                options={appKeyOptions}
                value={filters.app_key}
                withOnly
              />
              <div className="input-with-shortcuts__shortcuts">
                {applicationShortcuts.map((option, idx) => (
                  <div key={idx} onClick={() => handleShortcutClick('app_key', option as IOption)}>
                    <AppLogo
                      appLogoUrl={option.url}
                      className={classNames('shortcut', {
                        selected: !!filters.app_key?.find(({ value }) => option.value === value),
                      })}
                      size="small"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="input-with-shortcuts">
              <MultiSelectInput
                filterOption={startsWithFilter}
                isClearable
                label="Countries"
                onChange={(option) => onFilterChanged('country', option as IOption[])}
                options={countryWithFlagOptions}
                value={filters.country}
                withOnly
              />
              <div className="input-with-shortcuts__shortcuts">
                {countryShortcuts.map((option, idx) => (
                  <span
                    className={classNames('option-flag-icon fi shortcut', {
                      [`fi-${option.value.toLowerCase()}`]: true,
                      selected: !!filters.country?.find(({ value }) => option.value === value),
                    })}
                    key={idx}
                    onClick={() => handleShortcutClick('country', option as IOption)}
                  />
                ))}
              </div>
            </div>
            <div className="date-inputs">
              <DateInput
                endDate={filters.end_date}
                label="Date range"
                maxDate={TODAY}
                maxRangeDays={MAX_DAYS_RANGE}
                onDateRangeChange={onDateChanged}
                startDate={filters.start_date}
                withFilters
                withRange
                withTime={false}
              />
              <div className="date-inputs__shortcuts">
                <Button onClick={handleDateModeClick('d7')} outlined size={isFullScreen ? 'small' : 'medium'}>
                  D7
                </Button>
                <Button onClick={handleDateModeClick('d10')} outlined size={isFullScreen ? 'small' : 'medium'}>
                  D10
                </Button>
                <Button onClick={handleDateModeClick('d30')} outlined size={isFullScreen ? 'small' : 'medium'}>
                  D30
                </Button>
              </div>
            </div>
          </div>
          <div className="filters-row">
            <SelectInput
              components={{ Control: CustomControl, Option: CustomImageOption }}
              isClearable
              isSearchable
              label="Platform"
              onChange={(option) => onFilterChanged('device_type', option as IOption)}
              options={platformOptions}
              value={filters.device_type}
              wrapperClassName="filters-row__input filters-row__input--small"
            />
            <SelectInput
              isClearable
              label="UA source"
              onChange={(option) => onFilterChanged('ua_source', option as IOption)}
              options={sourceOptions}
              value={filters.ua_source}
              wrapperClassName="filters-row__input filters-row__input--small"
            />
            <SelectInput
              isClearable
              label="UA campaign"
              onChange={(option) => onFilterChanged('ua_campaign_id', option as IOption)}
              options={campaignOptions}
              value={filters.ua_campaign_id}
              wrapperClassName={classNames('filters-row__input', { 'filters-row__input--small': isFullScreen })}
            />
            <SelectInput
              isClearable
              label="UA adset"
              onChange={(option) => onFilterChanged('ua_adset_id', option as IOption)}
              options={adsetOptions}
              value={
                filters.ua_adset_id
                  ? adsetOptions?.find((option) => filters.ua_adset_id?.value === option.value)
                  : filters.ua_adset_id
              }
              wrapperClassName={classNames('filters-row__input', { 'filters-row__input--small': isFullScreen })}
            />
          </div>
        </div>
        <div className="controls-container">
          <div className="filters-row ">
            <MultiSelectInput
              className={classNames('filters-row__input', { 'filters-row__input--small': isFullScreen })}
              hideSelectedOptions={false}
              isClearable
              label="Aggregations"
              onChange={(option) => onFilterChanged('aggregations', option as IOption[])}
              options={aggregationOptions}
              required
              value={filters.aggregations}
              withOnly
            />
            {showTopCountries ? (
              <SelectInput
                isClearable
                label="Top countries"
                onChange={(option) => onFilterChanged('top_countries_count', option as IOption)}
                options={topCountriesCountOptions}
                value={filters.top_countries_count}
                wrapperClassName="filters-row__input filters-row__input--small"
              />
            ) : null}
          </div>
          <div className="filters-row filters-buttons">
            <Button disabled={isSubmitButtonDisabled || filters.aggregations.length === 0} onClick={onSubmit}>
              Fetch
            </Button>
            {!isFullScreen ? (
              <Button disabled={isSubmitButtonDisabled} onClick={onFullScreen} outlined>
                <MdFullscreen color="#ff5301" size={16} />
                Fullscreen
              </Button>
            ) : null}
            <Button disabled={isSubmitButtonDisabled} ghost onClick={onReset}>
              Reset
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UAFilters
